import { useState } from "react";
import { Checkbox, ConfirmDialog, Input } from "@v0y4ge/jrni-ui";
import type { NavApi } from "../../shell/router";
import type { UIEvent } from "../../lib/events-adapter";
import { useT } from "../../lib/i18n";
import { eventActions, serverMessage } from "./eventActions";
import { useNav } from "../../shell/router";
import { bookedCount } from "./EventLifecycle";

/**
 * The confirm step in front of "Cancel event" / "Delete draft" — shared by the
 * Events list row menu and the event drawer menu so both ask the same question.
 * Delete draft is a draft with no booked guests; anything else is a cancel.
 * The cancel says how many guests are booked and carries a "Notify booked guests"
 * choice (on by default) sent as `notify` with cancelEvent. The optional reason
 * is sent with the cancel. A refusal stays in the dialog with the server's message.
 */
export function CancelEventConfirm({ ev, nav, onClose, knownBooked }: { ev: UIEvent | null; nav: NavApi; onClose: () => void; /** The server refused an archive because guests are booked: never a discard, and the Notify choice is shown even if the count reads 0. */ knownBooked?: boolean }) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true); // ui-local — the per-cancel Notify choice
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = ev ? bookedCount(ev) : 0;
  const booked = count > 0 || !!knownBooked;
  const isDraft = ev?.status === "draft" && !booked;
  const nav2 = useNav();
  /* Say only what will happen (CC 0340). The server says whether a cancellation message goes out;
     until it does, the line promises nothing. notify:false withholds it even when it would send;
     notify:true cannot send a message that is switched off or not set up, so the choice is only
     offered when there are booked guests and the message is not known to be off. */
  const notice = ev?.cancellationNotice;
  const noticeOff = !!notice && !notice.sends;
  const offerNotify = !isDraft && booked && !noticeOff;
  const consequence = !ev ? undefined : isDraft
    ? t("events.cancelConfirm.deleteConsequenceArchive", "The draft leaves your event lists and moves to Archive.")
    : !booked
      ? t("events.cancelConfirm.noGuests", "No one is registered, so no one needs to be told.")
      : count === 0
        ? (noticeOff
          ? t("events.cancelConfirm.bookedNoticeOffUncounted", "Guests are booked. No cancellation message will go out, so they won't be notified.")
          : !notify
            ? t("events.cancelConfirm.bookedUncountedNotNotified", "Guests are booked. They won't be notified.")
            : t("events.cancelConfirm.bookedUncounted", "Guests are booked. They're notified if a cancellation message is set up in Messaging."))
      : noticeOff
        ? (notice!.reason === "switched_off"
          ? t("events.cancelConfirm.noticeOff", "The cancellation message is switched off, so {count, plural, one {the # registered guest} other {the # registered guests}} won't be notified.", { count })
          : t("events.cancelConfirm.noticeMissing", "No cancellation message is set up, so {count, plural, one {the # registered guest} other {the # registered guests}} won't be notified.", { count }))
        : !notify
          ? t("events.cancelConfirm.bookedNotNotified", "{count, plural, one {# guest is booked.} other {# guests are booked.}} They won't be notified.", { count })
          : notice && notice.sends
            ? t("events.cancelConfirm.consequence", "{count, plural, one {# registered guest will be notified.} other {# registered guests will be notified.}}", { count })
            : t("events.cancelConfirm.noticeUnknown", "{count, plural, one {# guest is registered.} other {# guests are registered.}} They're notified only if a cancellation message is set up in Messaging.", { count });
  const showMessagingLink = !isDraft && booked && notify && !(notice && notice.sends);
  const reset = () => { setReason(""); setNotify(true); setError(null); };
  const close = () => { if (!busy) { reset(); onClose(); } };
  const run = async () => { // awaits cancelEvent / discardEvent; a refusal keeps the dialog open
    if (!ev || busy) return;
    setBusy(true); setError(null);
    try {
      /* notify:true only after the choice was shown. Booked guests with no choice shown (message off)
         send false, which is what the dialog said; nobody booked has nobody to notify. */
      await eventActions.cancelOrDiscard(ev, nav, { reason, notify: offerNotify ? notify : !booked, mode: isDraft ? "discard" : "cancel" });
      setBusy(false); reset(); onClose();
    } catch (e) {
      setError(serverMessage(e) || t("events.actions.theEventIsUnchangedRetryInAMoment", "The event is unchanged. Retry in a moment."));
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog open={!!ev} tone="danger" busy={busy} testid="event-cancel-confirm"
      title={ev ? (isDraft ? t("events.cancelConfirm.deleteTitle", "Delete draft {title}?", { title: ev.title }) : t("events.cancelConfirm.title", "Cancel {title}?", { title: ev.title })) : ""}
      consequence={error ?? consequence}
      confirmLabel={isDraft ? t("events.cancelConfirm.deleteConfirm", "Delete draft") : t("events.cancelConfirm.confirm", "Cancel event")}
      cancelLabel={isDraft ? t("events.cancelConfirm.deleteKeep", "Keep draft") : t("events.cancelConfirm.keep", "Keep event")}
      onCancel={close} onConfirm={() => { void run(); }}>
      {offerNotify ? (
        <div style={{ marginBottom: 10 }} data-testid="event-cancel-notify">
          <Checkbox checked={notify} onChange={(on: boolean) => setNotify(on)} disabled={busy}
            label={count > 0 ? t("events.cancelConfirm.notifyBooked", "Notify booked guests ({count})", { count }) : t("events.cancelConfirm.notifyBookedUncounted", "Notify booked guests")} />
        </div>
      ) : null}
      {showMessagingLink ? (
        <button type="button" data-testid="event-cancel-messaging-link" onClick={() => { close(); nav2.go("messaging"); }}
          style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, font: "inherit", fontSize: 13, fontWeight: 600, color: "var(--jrni-color-primary-text, #5142C7)", textDecoration: "underline", cursor: "pointer", marginBottom: 10 }}>
          {t("events.cancelConfirm.openMessaging", "Set up the cancellation message in Messaging")}
        </button>
      ) : null}
      {isDraft ? null : (
        <Input label={t("events.cancelConfirm.reason", "Reason (optional)")} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} testid="event-cancel-reason" />
      )}
    </ConfirmDialog>
  );
}
